import type {
  AnimationBehavior,
  AnimationEffect,
  AnimationGroup,
  SlideTiming,
} from '../ir/timing.js';

/**
 * The render timeline — M3 stage B.
 *
 * `SlideTiming` says what the file asked for in OOXML's own vocabulary. This
 * says what the player should draw: numbers, in CSS pixels and degrees, on a
 * 0..1 timeline per step. Everything the player cannot express is resolved here,
 * once, where it is testable in Node, rather than in the browser.
 *
 * Frames are structured rather than CSS strings so one interpolator can settle
 * any step at any time — which is what makes seek, scrub and reverse work
 * without replaying an animation.
 */

export interface ClipInset {
  /** Percentages of the shape's own box, as in `inset(top right bottom left)`. */
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export interface TimelineFrame {
  /** Position within the step, 0..1. */
  readonly offset: number;
  readonly opacity?: number;
  /** CSS pixels, added to the shape's authored position. */
  readonly translateX?: number;
  readonly translateY?: number;
  readonly scaleX?: number;
  readonly scaleY?: number;
  /** Degrees, added to the shape's authored rotation. */
  readonly rotate?: number;
  readonly clip?: ClipInset;
  /** CSS colour applied to the shape's text. */
  readonly color?: string;
  /** CSS colour applied to the shape's fill. */
  readonly fill?: string;
  readonly visible?: boolean;
}

export type TimelineStepKind = 'entrance' | 'emphasis' | 'exit' | 'motion' | 'other';
export type TimelineEasing = 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';

export interface TimelineStep {
  readonly id: string;
  readonly shapeId: string;
  /** Set when the effect targets a paragraph range rather than the whole shape. */
  readonly paragraphs?: { readonly start: number; readonly end: number };
  readonly kind: TimelineStepKind;
  /** Start relative to the group, in milliseconds. */
  readonly startMs: number;
  readonly durationMs: number;
  readonly iterations: number;
  readonly alternate: boolean;
  readonly easing: TimelineEasing;
  readonly frames: readonly TimelineFrame[];
}

export interface TimelineGroup {
  readonly index: number;
  /** Runs on from the previous step instead of waiting for Next. */
  readonly auto: boolean;
  readonly delayMs: number;
  readonly durationMs: number;
  readonly steps: readonly TimelineStep[];
}

export interface TimelineTrigger {
  readonly id: string;
  readonly triggerShapeId: string;
  readonly durationMs: number;
  readonly steps: readonly TimelineStep[];
}

export interface SlideTimeline {
  readonly groups: readonly TimelineGroup[];
  readonly triggers: readonly TimelineTrigger[];
  readonly totalMs: number;
  /** Shapes whose entrance has not played yet, hidden before the timeline starts. */
  readonly initiallyHidden: readonly string[];
  /** `animation:*` constructs that did not survive, for the fidelity report. */
  readonly unsupported: readonly string[];
}

export interface TimelineContext {
  /** Stage size in CSS pixels; `ppt_x`/`ppt_y` and motion paths are fractions of it. */
  readonly stageWidth: number;
  readonly stageHeight: number;
  readonly boundsOf: (
    shapeId: string,
  ) => { readonly x: number; readonly y: number; readonly w: number; readonly h: number } | undefined;
}

const MOTION_SAMPLES = 24;

export function compileTimeline(
  timing: SlideTiming | undefined,
  ctx: TimelineContext,
): SlideTimeline | undefined {
  if (!timing || (timing.groups.length === 0 && timing.interactive.length === 0)) return undefined;

  const unsupported = new Set<string>(timing.unsupported);
  const hidden = new Set<string>();

  const groups = timing.groups
    .map((group) => compileGroup(group, ctx, unsupported, hidden))
    .filter((group): group is TimelineGroup => group !== undefined);

  const triggers = timing.interactive
    .map((sequence) => {
      const steps = compileSteps(sequence.effects, ctx, unsupported, hidden);
      if (steps.length === 0) return undefined;
      const trigger: TimelineTrigger = {
        id: sequence.id,
        triggerShapeId: sequence.triggerShapeId,
        durationMs: endOf(steps),
        steps,
      };
      return trigger;
    })
    .filter((trigger): trigger is TimelineTrigger => trigger !== undefined);

  // A timing tree whose every effect was dropped still returns a timeline: the
  // `unsupported` list is the only record of what the deck asked for and did not
  // get, and losing it here would make the fidelity report quietly wrong.
  return {
    groups,
    triggers,
    totalMs: groups.reduce((sum, g) => sum + g.delayMs + g.durationMs, 0),
    initiallyHidden: [...hidden].sort(),
    unsupported: [...unsupported].sort(),
  };
}

function compileGroup(
  group: AnimationGroup,
  ctx: TimelineContext,
  unsupported: Set<string>,
  hidden: Set<string>,
): TimelineGroup | undefined {
  const steps = compileSteps(group.effects, ctx, unsupported, hidden);
  if (steps.length === 0) return undefined;
  return {
    index: group.index,
    auto: group.auto,
    delayMs: group.delayMs,
    durationMs: endOf(steps),
    steps,
  };
}

function compileSteps(
  effects: readonly AnimationEffect[],
  ctx: TimelineContext,
  unsupported: Set<string>,
  hidden: Set<string>,
): TimelineStep[] {
  const steps: TimelineStep[] = [];
  for (const effect of effects) {
    const step = compileEffect(effect, ctx, unsupported);
    if (!step) continue;
    // An entrance means the shape is not on screen until its step runs.
    if (step.kind === 'entrance') hidden.add(step.shapeId);
    steps.push(step);
  }
  return steps;
}

function compileEffect(
  effect: AnimationEffect,
  ctx: TimelineContext,
  unsupported: Set<string>,
): TimelineStep | undefined {
  const shapeId = effect.target.shapeId;
  if (shapeId === '') {
    unsupported.add('animation:target:unresolved');
    return undefined;
  }

  const kind = kindOf(effect);
  if (kind === 'other' && effect.presetClass !== 'other') {
    unsupported.add(`animation:class:${effect.presetClass}`);
    return undefined;
  }

  const builder = new FrameBuilder(kind);
  let easing: TimelineEasing = 'linear';
  let iterations = 1;
  let alternate = false;

  for (const behavior of effect.behaviors) {
    applyBehavior(behavior, builder, ctx, shapeId, unsupported);
    if (behavior.timing.durationMs > 0) {
      easing = easingOf(behavior.timing.accelerate, behavior.timing.decelerate);
      if (Number.isFinite(behavior.timing.repeatCount)) {
        iterations = Math.max(iterations, Math.round(behavior.timing.repeatCount));
      } else {
        // An indefinite repeat is played as a long but finite loop: a course
        // that never settles can never be "complete".
        iterations = Math.max(iterations, 1);
        unsupported.add('animation:repeat:indefinite');
      }
      alternate = alternate || behavior.timing.autoReverse;
    }
  }

  const frames = builder.build(effect, unsupported);
  if (frames.length === 0) return undefined;

  return {
    id: effect.id,
    shapeId,
    ...(effect.target.paragraphs ? { paragraphs: effect.target.paragraphs } : {}),
    kind,
    startMs: effect.startMs,
    durationMs: Math.max(effect.durationMs, 1),
    iterations,
    alternate,
    easing,
    frames,
  };
}

function kindOf(effect: AnimationEffect): TimelineStepKind {
  switch (effect.presetClass) {
    case 'entrance':
      return 'entrance';
    case 'exit':
      return 'exit';
    case 'emphasis':
      return 'emphasis';
    case 'motionPath':
      return 'motion';
    case 'mediaCall':
    case 'verb':
      return 'other';
    default:
      // Files written without a preset class still animate; the behaviours say how.
      return effect.behaviors.some((b) => b.kind === 'motion') ? 'motion' : 'emphasis';
  }
}

/**
 * Accumulates one effect's behaviours into a start frame and an end frame, plus
 * any intermediate samples a motion path or keyframe list contributes.
 */
class FrameBuilder {
  #start: Mutable = {};
  #end: Mutable = {};
  #samples: { offset: number; frame: Mutable }[] = [];
  #touched = false;

  constructor(private readonly kind: TimelineStepKind) {}

  get touched(): boolean {
    return this.#touched;
  }

  pair(from: Partial<Mutable>, to: Partial<Mutable>): void {
    Object.assign(this.#start, from);
    Object.assign(this.#end, to);
    this.#touched = true;
  }

  sample(offset: number, frame: Mutable): void {
    this.#samples.push({ offset, frame });
    this.#touched = true;
  }

  build(effect: AnimationEffect, unsupported: Set<string>): TimelineFrame[] {
    if (!this.#touched) {
      // Nothing in the file told us how this effect looks. An entrance or exit
      // still has to change whether the shape is on screen, so it falls back to
      // the one behaviour every such effect shares; anything else is dropped and
      // reported rather than invented.
      if (this.kind === 'entrance') this.pair({ opacity: 0, visible: true }, { opacity: 1, visible: true });
      else if (this.kind === 'exit') this.pair({ opacity: 1, visible: true }, { opacity: 0, visible: false });
      else {
        unsupported.add(`animation:effect:${effect.presetClass}:${effect.presetId ?? 'unknown'}`);
        return [];
      }
    }

    if (this.kind === 'entrance') {
      this.#start.visible = true;
      this.#end.visible = true;
    } else if (this.kind === 'exit') {
      this.#start.visible = true;
      if (this.#end.visible === undefined) this.#end.visible = false;
    }

    const frames: TimelineFrame[] = [{ offset: 0, ...clean(this.#start) }];
    for (const sample of this.#samples.sort((a, b) => a.offset - b.offset)) {
      if (sample.offset <= 0 || sample.offset >= 1) continue;
      frames.push({ offset: sample.offset, ...clean({ ...this.#start, ...sample.frame }) });
    }
    frames.push({ offset: 1, ...clean(this.#end) });
    return frames;
  }
}

type Mutable = {
  opacity?: number;
  translateX?: number;
  translateY?: number;
  scaleX?: number;
  scaleY?: number;
  rotate?: number;
  clip?: ClipInset;
  color?: string;
  fill?: string;
  visible?: boolean;
};

function clean(frame: Mutable): Mutable {
  const out: Mutable = {};
  for (const [key, value] of Object.entries(frame)) {
    if (value !== undefined) (out as Record<string, unknown>)[key] = value;
  }
  return out;
}

function applyBehavior(
  behavior: AnimationBehavior,
  builder: FrameBuilder,
  ctx: TimelineContext,
  shapeId: string,
  unsupported: Set<string>,
): void {
  switch (behavior.kind) {
    case 'set':
      applySet(behavior.attribute, behavior.to, builder);
      return;
    case 'filter':
      applyFilter(behavior.filter, behavior.transition, builder, ctx.boundsOf(shapeId), unsupported);
      return;
    case 'animate':
      applyAnimate(behavior, builder, ctx, shapeId, unsupported);
      return;
    case 'scale': {
      const from = behavior.from ? { x: behavior.from.x / 100000, y: behavior.from.y / 100000 } : undefined;
      const to = behavior.to
        ? { x: behavior.to.x / 100000, y: behavior.to.y / 100000 }
        : behavior.by
          ? { x: behavior.by.x / 100000, y: behavior.by.y / 100000 }
          : undefined;
      if (!to) return;
      builder.pair(
        { scaleX: from?.x ?? 1, scaleY: from?.y ?? 1 },
        { scaleX: to.x, scaleY: to.y },
      );
      return;
    }
    case 'rotate': {
      const from = behavior.from !== undefined ? behavior.from / 60000 : 0;
      const to =
        behavior.to !== undefined
          ? behavior.to / 60000
          : behavior.by !== undefined
            ? from + behavior.by / 60000
            : undefined;
      if (to === undefined) return;
      builder.pair({ rotate: from }, { rotate: to });
      return;
    }
    case 'color': {
      if (behavior.to === undefined) {
        unsupported.add('animation:color:relative');
        return;
      }
      const css = `#${behavior.to}`;
      if (/color$/i.test(behavior.attribute) && !/fill/i.test(behavior.attribute)) {
        builder.pair({ color: css }, { color: css });
      } else {
        builder.pair({ fill: css }, { fill: css });
      }
      return;
    }
    case 'motion':
      applyMotion(behavior, builder, ctx, unsupported);
      return;
    case 'command':
      unsupported.add('animation:behavior:command');
      return;
  }
}

function applySet(attribute: string, to: string, builder: FrameBuilder): void {
  switch (attribute) {
    case 'style.visibility':
      builder.pair({ visible: to === 'visible' }, { visible: to === 'visible' });
      return;
    case 'style.opacity': {
      const value = Number.parseFloat(to);
      if (Number.isFinite(value)) builder.pair({ opacity: value }, { opacity: value });
      return;
    }
    default:
      return;
  }
}

/**
 * `p:animEffect@filter` — the SMIL transition filters PowerPoint writes.
 *
 * Only the filters that map onto something a browser can draw exactly are
 * honoured. The rest degrade to a fade and are reported: an approximated wipe
 * that the author never asked for is worse than an honest one-line warning.
 */
function applyFilter(
  filter: string,
  transition: 'in' | 'out' | 'none',
  builder: FrameBuilder,
  bounds: { readonly w: number; readonly h: number } | undefined,
  unsupported: Set<string>,
): void {
  const entering = transition !== 'out';
  // A filter list is `a(x);b(y)` with `|` alternatives; the first is enough.
  const first = filter.split(';')[0]?.split('|')[0]?.trim() ?? '';
  const match = /^([a-zA-Z]+)(?:\(([^)]*)\))?$/.exec(first);
  const name = (match?.[1] ?? '').toLowerCase();
  const argument = (match?.[2] ?? '').toLowerCase();

  const fade = (): void =>
    builder.pair({ opacity: entering ? 0 : 1 }, { opacity: entering ? 1 : 0 });

  switch (name) {
    case 'fade':
      fade();
      return;
    case 'wipe': {
      const hiddenClip = wipeClip(argument);
      if (!hiddenClip) {
        unsupported.add(`animation:filter:${first}`);
        fade();
        return;
      }
      builder.pair(
        { clip: entering ? hiddenClip : FULL_CLIP },
        { clip: entering ? FULL_CLIP : hiddenClip },
      );
      return;
    }
    case 'barn': {
      const closed = argument.includes('vertical')
        ? { top: 0, right: 50, bottom: 0, left: 50 }
        : { top: 50, right: 0, bottom: 50, left: 0 };
      builder.pair({ clip: entering ? closed : FULL_CLIP }, { clip: entering ? FULL_CLIP : closed });
      return;
    }
    case 'box':
    case 'diamond':
    case 'plus': {
      // Only two of the four combinations are a shrinking rectangle around the
      // centre, which is all `inset()` can draw: revealing outwards on the way
      // in, and concealing inwards on the way out. The other two reveal from the
      // edges towards the centre — a hole, not an inset — and are approximated
      // with the same movement and reported.
      const collapsed = { top: 50, right: 50, bottom: 50, left: 50 };
      const expressible = entering ? argument !== 'in' : argument !== 'out';
      if (!expressible) unsupported.add(`animation:filter:${first}`);
      builder.pair(
        { clip: entering ? collapsed : FULL_CLIP },
        { clip: entering ? FULL_CLIP : collapsed },
      );
      return;
    }
    case 'slide': {
      const direction = slideOffset(argument);
      if (!direction) {
        unsupported.add(`animation:filter:${first}`);
        fade();
        return;
      }
      // The shape slides in from just outside its own box, so the distance is a
      // multiple of that box rather than a fixed number of pixels.
      const offscreen = {
        translateX: direction.x * (bounds?.w ?? 0),
        translateY: direction.y * (bounds?.h ?? 0),
      };
      const onstage = { translateX: 0, translateY: 0 };
      builder.pair(entering ? offscreen : onstage, entering ? onstage : offscreen);
      return;
    }
    case 'dissolve':
      // A per-pixel dissolve is not expressible in CSS; a fade is the closest
      // honest substitute and is recorded as such.
      unsupported.add('animation:filter:dissolve');
      fade();
      return;
    default:
      unsupported.add(`animation:filter:${name || 'unknown'}`);
      fade();
      return;
  }
}

const FULL_CLIP: ClipInset = { top: 0, right: 0, bottom: 0, left: 0 };

/** The clip that hides the shape before a wipe in the given direction reveals it. */
function wipeClip(direction: string): ClipInset | undefined {
  switch (direction) {
    case 'up':
      return { top: 100, right: 0, bottom: 0, left: 0 };
    case 'down':
      return { top: 0, right: 0, bottom: 100, left: 0 };
    case 'left':
      return { top: 0, right: 0, bottom: 0, left: 100 };
    case 'right':
      return { top: 0, right: 100, bottom: 0, left: 0 };
    default:
      return undefined;
  }
}

/** Off-stage offsets are expressed as multiples of the shape's own box. */
function slideOffset(direction: string): { x: number; y: number } | undefined {
  switch (direction) {
    case 'fromleft':
      return { x: -1, y: 0 };
    case 'fromright':
      return { x: 1, y: 0 };
    case 'fromtop':
      return { x: 0, y: -1 };
    case 'frombottom':
      return { x: 0, y: 1 };
    default:
      return undefined;
  }
}

function applyAnimate(
  behavior: Extract<AnimationBehavior, { kind: 'animate' }>,
  builder: FrameBuilder,
  ctx: TimelineContext,
  shapeId: string,
  unsupported: Set<string>,
): void {
  const bounds = ctx.boundsOf(shapeId);
  const values = valueSequence(behavior);
  if (values.length < 2) return;

  const first = values[0];
  const last = values[values.length - 1];
  if (!first || !last) return;

  switch (behavior.attribute) {
    case 'ppt_x':
    case 'ppt_y': {
      if (!bounds) {
        unsupported.add('animation:target:unresolved');
        return;
      }
      const axis = behavior.attribute === 'ppt_x' ? 'x' : 'y';
      const stage = axis === 'x' ? ctx.stageWidth : ctx.stageHeight;
      const size = axis === 'x' ? bounds.w : bounds.h;
      // `ppt_x` is the fraction of the stage occupied by the shape's centre.
      const current = ((axis === 'x' ? bounds.x : bounds.y) + size / 2) / (stage || 1);
      const toOffset = (raw: string): number | undefined => {
        const fraction = resolveFraction(raw, current);
        return fraction === undefined ? undefined : (fraction - current) * stage;
      };
      const from = toOffset(first.value);
      const to = toOffset(last.value);
      if (from === undefined || to === undefined) {
        unsupported.add(`animation:formula:${behavior.attribute}`);
        return;
      }
      const key = axis === 'x' ? 'translateX' : 'translateY';
      builder.pair({ [key]: from }, { [key]: to });
      for (const value of values.slice(1, -1)) {
        const offset = toOffset(value.value);
        if (offset !== undefined) builder.sample(value.time, { [key]: offset });
      }
      return;
    }
    case 'style.opacity': {
      const from = Number.parseFloat(first.value);
      const to = Number.parseFloat(last.value);
      if (!Number.isFinite(from) || !Number.isFinite(to)) return;
      builder.pair({ opacity: from }, { opacity: to });
      return;
    }
    case 'style.rotation':
    case 'r': {
      const from = Number.parseFloat(first.value);
      const to = Number.parseFloat(last.value);
      if (!Number.isFinite(from) || !Number.isFinite(to)) return;
      builder.pair({ rotate: from }, { rotate: to });
      return;
    }
    case 'ppt_w':
    case 'ppt_h': {
      const from = Number.parseFloat(first.value);
      const to = Number.parseFloat(last.value);
      if (!Number.isFinite(from) || !Number.isFinite(to)) return;
      const key = behavior.attribute === 'ppt_w' ? 'scaleX' : 'scaleY';
      builder.pair({ [key]: from }, { [key]: to });
      return;
    }
    default:
      unsupported.add(`animation:attribute:${behavior.attribute}`);
      return;
  }
}

function valueSequence(
  behavior: Extract<AnimationBehavior, { kind: 'animate' }>,
): { time: number; value: string }[] {
  if (behavior.keyframes.length > 0) {
    return behavior.keyframes.map((k) => ({ time: k.time, value: k.value }));
  }
  const out: { time: number; value: string }[] = [];
  if (behavior.from !== undefined) out.push({ time: 0, value: behavior.from });
  if (behavior.to !== undefined) out.push({ time: 1, value: behavior.to });
  return out;
}

/**
 * Resolves a position value.
 *
 * `#ppt_x` and `#ppt_y` mean "where the shape already is". Anything else has to
 * be a plain number: OOXML allows arithmetic formulae here, and evaluating them
 * badly would move a shape somewhere the author never put it, so an unrecognised
 * expression is reported instead of guessed at.
 */
function resolveFraction(raw: string, current: number): number | undefined {
  const trimmed = raw.trim();
  if (/^#ppt_[xy]$/.test(trimmed)) return current;
  const value = Number.parseFloat(trimmed);
  return Number.isFinite(value) ? value : undefined;
}

function applyMotion(
  behavior: Extract<AnimationBehavior, { kind: 'motion' }>,
  builder: FrameBuilder,
  ctx: TimelineContext,
  unsupported: Set<string>,
): void {
  if (behavior.path) {
    const points = samplePath(behavior.path, unsupported);
    if (points.length >= 2) {
      const start = points[0] ?? { x: 0, y: 0 };
      const end = points[points.length - 1] ?? start;
      builder.pair(
        { translateX: start.x * ctx.stageWidth, translateY: start.y * ctx.stageHeight },
        { translateX: end.x * ctx.stageWidth, translateY: end.y * ctx.stageHeight },
      );
      points.slice(1, -1).forEach((point, i) => {
        builder.sample((i + 1) / (points.length - 1), {
          translateX: point.x * ctx.stageWidth,
          translateY: point.y * ctx.stageHeight,
        });
      });
      return;
    }
  }
  if (behavior.by) {
    builder.pair(
      { translateX: 0, translateY: 0 },
      { translateX: behavior.by.x * ctx.stageWidth, translateY: behavior.by.y * ctx.stageHeight },
    );
    return;
  }
  unsupported.add('animation:motion:unreadable');
}

interface Point {
  x: number;
  y: number;
}

/**
 * Samples an OOXML motion path into points in slide fractions.
 *
 * The grammar is SVG's, restricted: `M` move, `L` line, `C` cubic, `Z` close,
 * `E` end. Curves are flattened rather than kept as curves so the player has a
 * single interpolation model for every kind of movement.
 */
export function samplePath(path: string, unsupported?: Set<string>): Point[] {
  const tokens = path.trim().split(/[\s,]+/).filter((t) => t !== '');
  const points: Point[] = [];
  let cursor: Point = { x: 0, y: 0 };
  let subpathStart: Point = { x: 0, y: 0 };
  let i = 0;

  const number = (): number | undefined => {
    const value = Number.parseFloat(tokens[i] ?? '');
    if (!Number.isFinite(value)) return undefined;
    i += 1;
    return value;
  };

  while (i < tokens.length) {
    const command = tokens[i];
    i += 1;
    switch (command) {
      case 'M':
      case 'm': {
        const x = number();
        const y = number();
        if (x === undefined || y === undefined) return points;
        cursor = command === 'M' ? { x, y } : { x: cursor.x + x, y: cursor.y + y };
        subpathStart = cursor;
        points.push(cursor);
        break;
      }
      case 'L':
      case 'l': {
        const x = number();
        const y = number();
        if (x === undefined || y === undefined) return points;
        cursor = command === 'L' ? { x, y } : { x: cursor.x + x, y: cursor.y + y };
        points.push(cursor);
        break;
      }
      case 'C':
      case 'c': {
        const coords: number[] = [];
        for (let n = 0; n < 6; n += 1) {
          const value = number();
          if (value === undefined) return points;
          coords.push(value);
        }
        const [c1x, c1y, c2x, c2y, ex, ey] = coords as [number, number, number, number, number, number];
        const rel = command === 'c';
        const p1 = { x: rel ? cursor.x + c1x : c1x, y: rel ? cursor.y + c1y : c1y };
        const p2 = { x: rel ? cursor.x + c2x : c2x, y: rel ? cursor.y + c2y : c2y };
        const end = { x: rel ? cursor.x + ex : ex, y: rel ? cursor.y + ey : ey };
        for (let step = 1; step <= MOTION_SAMPLES; step += 1) {
          points.push(cubicAt(cursor, p1, p2, end, step / MOTION_SAMPLES));
        }
        cursor = end;
        break;
      }
      case 'Z':
      case 'z':
        points.push(subpathStart);
        cursor = subpathStart;
        break;
      case 'E':
      case 'e':
        break;
      default:
        unsupported?.add(`animation:motion:command:${command ?? '?'}`);
        return points;
    }
  }
  return points;
}

function cubicAt(p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return {
    x: a * p0.x + b * p1.x + c * p2.x + d * p3.x,
    y: a * p0.y + b * p1.y + c * p2.y + d * p3.y,
  };
}

function easingOf(accelerate: number, decelerate: number): TimelineEasing {
  if (accelerate > 0 && decelerate > 0) return 'ease-in-out';
  if (accelerate > 0) return 'ease-in';
  if (decelerate > 0) return 'ease-out';
  return 'linear';
}

function endOf(steps: readonly TimelineStep[]): number {
  return steps.reduce((max, s) => Math.max(max, s.startMs + s.durationMs * s.iterations), 0);
}

/**
 * The value of every animated property at `t` milliseconds into a step.
 *
 * Lives here, next to the frames, because the player and the tests must agree
 * exactly on what "settled" means — that agreement is what lets Previous restore
 * a state instead of replaying it.
 */
export function frameAt(step: TimelineStep, elapsedMs: number): TimelineFrame {
  const span = Math.max(1, step.durationMs);
  const total = span * Math.max(1, step.iterations);
  const clamped = Math.max(0, Math.min(total, elapsedMs));
  const iteration = Math.min(step.iterations - 1, Math.floor(clamped / span));
  let local = (clamped - iteration * span) / span;
  if (clamped >= total) local = 1;
  if (step.alternate && iteration % 2 === 1) local = 1 - local;

  const eased = ease(step.easing, local);
  const frames = step.frames;
  let before = frames[0];
  let after = frames[frames.length - 1];
  for (let i = 0; i < frames.length - 1; i += 1) {
    const a = frames[i];
    const b = frames[i + 1];
    if (!a || !b) continue;
    if (eased >= a.offset && eased <= b.offset) {
      before = a;
      after = b;
      break;
    }
  }
  if (!before || !after) return { offset: eased };

  const span2 = after.offset - before.offset;
  const local2 = span2 === 0 ? 0 : (eased - before.offset) / span2;
  return {
    offset: eased,
    ...pick('opacity', before, after, local2),
    ...pick('translateX', before, after, local2),
    ...pick('translateY', before, after, local2),
    ...pick('scaleX', before, after, local2),
    ...pick('scaleY', before, after, local2),
    ...pick('rotate', before, after, local2),
    ...pickClip(before, after, local2),
    ...(after.color !== undefined || before.color !== undefined
      ? { color: local2 >= 1 ? (after.color ?? before.color) : (before.color ?? after.color) }
      : {}),
    ...(after.fill !== undefined || before.fill !== undefined
      ? { fill: local2 >= 1 ? (after.fill ?? before.fill) : (before.fill ?? after.fill) }
      : {}),
    ...(before.visible !== undefined || after.visible !== undefined
      ? { visible: local2 >= 1 ? (after.visible ?? before.visible) : (before.visible ?? after.visible) }
      : {}),
  };
}

/** The state a step leaves behind — what Previous restores without replaying. */
export function settledFrame(step: TimelineStep): TimelineFrame {
  return frameAt(step, step.durationMs * Math.max(1, step.iterations));
}

type NumericKey = 'opacity' | 'translateX' | 'translateY' | 'scaleX' | 'scaleY' | 'rotate';

function pick(
  key: NumericKey,
  before: TimelineFrame,
  after: TimelineFrame,
  t: number,
): Partial<Record<NumericKey, number>> {
  const a = before[key];
  const b = after[key];
  if (a === undefined && b === undefined) return {};
  if (a === undefined) return { [key]: b } as Partial<Record<NumericKey, number>>;
  if (b === undefined) return { [key]: a } as Partial<Record<NumericKey, number>>;
  return { [key]: a + (b - a) * t } as Partial<Record<NumericKey, number>>;
}

function pickClip(before: TimelineFrame, after: TimelineFrame, t: number): { clip?: ClipInset } {
  const a = before.clip;
  const b = after.clip;
  if (!a && !b) return {};
  if (!a) return { clip: b };
  if (!b) return { clip: a };
  return {
    clip: {
      top: a.top + (b.top - a.top) * t,
      right: a.right + (b.right - a.right) * t,
      bottom: a.bottom + (b.bottom - a.bottom) * t,
      left: a.left + (b.left - a.left) * t,
    },
  };
}

function ease(kind: TimelineEasing, t: number): number {
  switch (kind) {
    case 'ease-in':
      return t * t;
    case 'ease-out':
      return t * (2 - t);
    case 'ease-in-out':
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    default:
      return t;
  }
}
